import { useState, useCallback, useEffect, useReducer, useRef } from 'react';
import { chatApi, apiClient, attachmentApi } from '@/services';
import { handleError, ApiStatusError } from '@/errors';
import type { Message as ApiMessage, MessagesResponse, AttachmentContentsResponse } from '@/services/api';
import type { Message } from '@/types';
import { useAuth } from './useAuth';
import { messageReducer } from './messageReducer';
import { sideBarReducer } from './sideBarReducer';
import { processAttachmentContents } from '@/utils/attachmentProcessor';
import { extractMessageContent } from '@/utils';
import { v4 as uuidv4 } from 'uuid';
import { ImageProcessingService } from '@/services/imageProcessing';

// Lightweight helpers to simplify loadMessages
const prepareBaseMessagesFromApi = (apiMessages: ApiMessage[]): Message[] => {
  const result: Message[] = [];
  for (let i = 0; i < apiMessages.length; i++) {
    result.push(convertApiMessageToMessage(apiMessages[i], i));
  }
  return result;
};

const buildAttachmentIndex = (apiMessages: ApiMessage[]): {
  order: string[];
  attachmentsMap: Map<string, string[]>;
} => {
  const order: string[] = [];
  const attachmentsMap = new Map<string, string[]>();
  for (const m of apiMessages) {
    order.push(m.id);
    if (m.attachments?.length) {
      attachmentsMap.set(m.id, m.attachments.map(a => a.id));
    }
  }
  return { order, attachmentsMap };
};

const getLastNWithAttachmentsIndices = (apiMessages: ApiMessage[], n: number): Array<{ id: string; index: number }> => {
  const out: Array<{ id: string; index: number }> = [];
  for (let i = apiMessages.length - 1; i >= 0 && out.length < n; i--) {
    const msg = apiMessages[i];
    if (msg.attachments?.length) {
      out.push({ id: msg.id, index: i });
    }
  }
  return out;
};

// Helper to determine message role when API doesn't provide reliable type info
const determineMessageRole = (apiMessage: ApiMessage, messageIndex: number): 'user' | 'assistant' => {
  // Strategy 1: Use API type if available (handle both string and numeric formats)
  // The API returns 0 for user and 1 for AI (as numbers, not strings)
  const messageType = apiMessage.type;
  
  // Check for user types (flexible comparison)
  if (messageType === 'user' || String(messageType) === '0') {
    return 'user';
  }
  
  // Check for AI types (flexible comparison)
  if (messageType === 'ai' || String(messageType) === '1') {
    return 'assistant';
  }
  
  // Strategy 2: Check for user-uploaded attachments
  const hasUserUploads = apiMessage.attachments?.some(att => att.source === 'user_upload');
  if (hasUserUploads) {
    return 'user';
  }
  
  // Strategy 3: Status-based detection
  if (apiMessage.status === 'sent') {
    return 'user';
  }
  if (apiMessage.status === 'completed') {
    return 'assistant';
  }
  
  // Strategy 4: Fallback to alternating pattern
  return messageIndex % 2 === 0 ? 'user' : 'assistant';
};

// Helper to convert API message to internal format
const convertApiMessageToMessage = (apiMessage: ApiMessage, messageIndex: number): Message => {
  const role = determineMessageRole(apiMessage, messageIndex);
  
  const convertedMessage: Message = {
    id: apiMessage.id,
    content: extractMessageContent(apiMessage.text),
    role,
    timestamp: new Date(apiMessage.created_at),
  };
  
  return convertedMessage;
};

interface UseChatMessagesReturn {
  messages: Message[];
  isTyping: boolean;
  isLoading: boolean;
  error: string | null;
  errorDetails?: {
    title?: string;
    detail?: string;
    traceId?: string;
    instance?: string;
  };
  currentChatId: number | null;
  sideBarReload: boolean;
  loadMessages: (chatId: number) => Promise<void>;
  sendMessage: (content: string, files?: File[], isInpaintingMode?: boolean) => Promise<void>;
  clearMessages: () => void;
  retryLastMessage: () => Promise<void>;
  clearError: () => void;
  isRetrying: boolean;
  loadOlderAttachments: (batchSize?: number) => Promise<void>;
  isLoadingOlderAttachments: boolean;
}

export function useChatMessages(initialChatId: number | null = null): UseChatMessagesReturn {
  const { getAccessToken } = useAuth();
  
  const [state, dispatch] = useReducer(messageReducer, {
    messages: [],
    isTyping: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<{
    title?: string;
    detail?: string;
    traceId?: string;
    instance?: string;
  } | undefined>(undefined);
  const [isRetrying, setIsRetrying] = useState(false);
  const [lastFailedMessage, setLastFailedMessage] = useState<{
    content: string;
    files?: File[];
    isInpaintingMode?: boolean;
  } | null>(null);
  const [currentChatId, setCurrentChatId] = useState<number | null>(initialChatId);
  const [isLoadingOlderAttachments, setIsLoadingOlderAttachments] = useState(false);

  //using dispatcher to reload the sidebar
  const [sideBarState, sideBarDispatcher] = useReducer(sideBarReducer, {
    reloadSideBar: false
  });

  const currentChatIdRef = useRef<number | null>(currentChatId);
  const loadedInitialChatIdRef = useRef<number | null>(null);
  const getAccessTokenRef = useRef(getAccessToken);
  // Refs to support lazy attachment hydration
  const attachmentsByMessageIdRef = useRef<Map<string, string[]>>(new Map());
  const messageOrderRef = useRef<string[]>([]);
  const hydratedAttachmentMessageIdsRef = useRef<Set<string>>(new Set());
  const baseMessagesRef = useRef<Message[]>([]);
  const loadedFromIndexRef = useRef<number>(0); // index of first currently rendered message in baseMessagesRef

  // Update refs when values change
  useEffect(() => {
    currentChatIdRef.current = currentChatId;
  }, [currentChatId]);

  useEffect(() => {
    getAccessTokenRef.current = getAccessToken;
  }, [getAccessToken]);

  // Load messages for a specific chat (moved below helpers to avoid TDZ/lint warnings)


  // Helper function to process uploaded files and create attachments
  const processFilesForUpload = useCallback(async (files: File[], isInpaintingMode: boolean = false) => {
    // Convert Files to Blobs and create object URLs
    const imageBlobs: Blob[] = [];
    const imageUrls: string[] = [];
    
    // Also convert to base64 for API
    const imagesBase64 = await ImageProcessingService.filesToBase64WithMetadata(files);
    
    for (const file of files) {
      if (file.type.startsWith('image/')) {
        const blob = new Blob([file], { type: file.type });
        const objectUrl = URL.createObjectURL(blob);
        imageBlobs.push(blob);
        imageUrls.push(objectUrl);
      }
    }
    
    // Create attachments for API
    // For inpainting: original image should be Type: 0, mask should be Type: 1
    // For regular mode: all images should be Type: 0
    const attachmentsForApi = imagesBase64.map((image, index) => {
      let attachmentType = 0; // Default to Type: 0 for all images
      
      if (isInpaintingMode && files.length >= 2) {
        // In inpainting mode with 2+ files:
        // First file (index 0) = original image = Type: 0
        // Second file (index 1) = mask = Type: 1
        // Any additional files = Type: 0
        attachmentType = (index === 1) ? 1 : 0;
      }
      
      return {
        Type: attachmentType,
        ContentType: image.metadata.type,
        Content: image.base64 // base64 is already clean without data URL prefix
      };
    });
    
    return { attachmentsForApi, imageBlobs, imageUrls };
  }, []);

  // Helper function to fetch and update message attachments
  const fetchAndUpdateAttachments = useCallback(async (
    apiMessage: any, 
    convertedMessage: Message, 
    currentToken: string
  ) => {
    if (!apiMessage?.attachments?.length) return;

    const attachmentIds = apiMessage.attachments.map((item: any) => item.id);
    
    try {
      const getAttachmentResponse = await attachmentApi.getAttachment(attachmentIds, currentToken);
      const { imageBlobs, imageUrls, allAttachments } = processAttachmentContents(getAttachmentResponse);
      
      // For user messages, match by content since ID might be different
      let targetMessageId = convertedMessage.id;
      if (convertedMessage.role === 'user') {
        let localUserMessage: Message | undefined;
        for (let i = state.messages.length - 1; i >= 0; i--) {
          const msg = state.messages[i];
          if (
            msg.role === 'user' &&
            msg.content === convertedMessage.content &&
            msg.imageUrls && msg.imageUrls.length > 0
          ) {
            localUserMessage = msg;
            break;
          }
        }

        if (localUserMessage) {
          targetMessageId = localUserMessage.id;
        }
      }

      dispatch({
        type: 'UPDATE_MESSAGE_ATTACHMENTS',
        payload: { messageId: targetMessageId, imageBlobs, imageUrls, allAttachments }
      });
    } catch (error) {
      await handleError(error, {
        setError: () => {}, // No state to set for attachment errors, just log
        context: `Fetching attachment contents for ${convertedMessage.role} message`
      });
    }
  }, [state.messages]);

  // Helper function to create user message with attachments
  const createUserMessageWithAttachments = useCallback(async (
    content: string,
    files?: File[],
    isInpaintingMode: boolean = false
  ): Promise<{ 
    userMessage: Message; 
    attachmentsForApi: Array<{ Type: number; ContentType: string; Content: string; }>;
  }> => {
    const userMessage: Message = {
      id: uuidv4(),
      content: content.trim(),
      role: 'user',
      timestamp: new Date(),
    };

    let attachmentsForApi: Array<{ Type: number; ContentType: string; Content: string; }> = [];

    if (files && files.length > 0) {
      const { attachmentsForApi: processedAttachments, imageBlobs, imageUrls } = await processFilesForUpload(files, isInpaintingMode);
      attachmentsForApi = processedAttachments;
      
      if (imageBlobs.length > 0) {
        userMessage.imageBlobs = imageBlobs;
        userMessage.imageUrls = imageUrls;
      }
    }

    return { userMessage, attachmentsForApi };
  }, [processFilesForUpload]);

  // Helper function to process API response messages
  const processApiResponseMessages = useCallback(async (
    messageArray: ApiMessage[],
    currentToken?: string
  ): Promise<void> => {
    const convertedMessages = messageArray.map((apiMessage, index) => convertApiMessageToMessage(apiMessage, index));

    // Update chat ID if this was a new chat
    if (!currentChatId && messageArray.length > 0 && messageArray[0].chatId) {
      const newChatId = messageArray[0].chatId;
      setCurrentChatId(newChatId);
    }

    // Process each message
    for (let i = 0; i < messageArray.length; i++) {
      const apiMessage = messageArray[i];
      const convertedMessage = convertedMessages[i];
      
      const expectedImageCount = apiMessage?.attachments?.length || 0;
      const messageWithExpectedCount = {
        ...convertedMessage,
        expectedImageCount: expectedImageCount > 0 ? expectedImageCount : undefined
      };

      // Add assistant messages to UI (user messages are already added locally)
      if (convertedMessage.role === 'assistant') {
        dispatch({ type: 'ADD_AI_MESSAGE', payload: messageWithExpectedCount });
      }

      // Fetch attachments if present
      if (apiMessage?.attachments?.length > 0) {
        await fetchAndUpdateAttachments(apiMessage, convertedMessage, currentToken ?? '');
      }
    }

    // Set typing to false if no assistant message was added
    const aiMessages = convertedMessages.filter(msg => msg.role === 'assistant');
    if (aiMessages.length === 0) {
      dispatch({ type: 'SET_TYPING', payload: false });
    }
  }, [currentChatId, fetchAndUpdateAttachments]);

  // Helper function to get access token with error handling
  const getValidAccessToken = useCallback(async (): Promise<string | undefined> => {
    // In mock mode there is intentionally no authenticated account/token.
    return await getAccessTokenRef.current();
  }, []);

  // Enhanced error handler that captures error details (placed above to avoid TDZ)
  const setErrorWithDetails = useCallback((errorMessage: string, details?: {
    title?: string;
    detail?: string;
    traceId?: string;
    instance?: string;
  }) => {
    setError(errorMessage);
    setErrorDetails(details);
  }, []);

  // Helper: determine if send should be blocked (no content/files or already typing)
  const isSendBlocked = (content: string, files: File[] | undefined, isTyping: boolean): boolean => {
    const noText = !content.trim();
    const noFiles = !files || files.length === 0;
    return (noText && noFiles) || isTyping;
  };

  // Helper: compute request type from files and inpainting mode
  const computeRequestType = (files?: File[], isInpaintingMode: boolean = false): string => {
    if (isInpaintingMode) return 'inpainting';
    if (files && files.length > 0) {
      const imageFiles = files.filter(file => file.type.startsWith('image/'));
      if (imageFiles.length > 1) return 'images_to_image';
      if (imageFiles.length === 1) return 'image_to_image';
    }
    return 'text_to_image';
  };

  // Helper: normalize and process API response messages
  const handleMessagesFromApi = useCallback(async (
    messages: MessagesResponse,
    currentToken?: string
  ): Promise<void> => {
    const messageArray: ApiMessage[] = Array.isArray(messages) ? messages : [messages];
    await processApiResponseMessages(messageArray, currentToken);
  }, [processApiResponseMessages]);

  // Helpers to further reduce cognitive complexity in loadMessages
  const computeInitialSubsetFromBase = (baseMessages: Message[], count: number): Message[] => {
    const total = baseMessages.length;
    const initialCount = Math.min(count, total);
    const startIndex = Math.max(0, total - initialCount);
    loadedFromIndexRef.current = startIndex;
    return baseMessages.slice(startIndex, total);
  };

  const renderSubset = (subset: Message[]): void => {
    dispatch({ type: 'CLEAR_MESSAGES' });
    for (const msg of subset) {
      if (msg.role === 'user') {
        dispatch({ type: 'LOAD_HISTORICAL_USER_MESSAGE', payload: msg });
      } else {
        dispatch({ type: 'LOAD_HISTORICAL_AI_MESSAGE', payload: msg });
      }
    }
  };

  const hydrateByIndices = async (
    indices: Array<{ id: string; index: number }>,
    attachmentsMap: Map<string, string[]>,
    baseMessages: Message[],
    chatId: number,
    accessToken?: string
  ): Promise<void> => {
    if (!indices.length) return;
    const idsToFetch: string[] = [];
    for (const item of indices) {
      const ids = attachmentsMap.get(item.id);
      if (ids?.length) idsToFetch.push(...ids);
    }
    if (!idsToFetch.length) return;

    if (currentChatIdRef.current !== chatId) return; // stale guard
    const contents = await attachmentApi.getAttachment(idsToFetch, accessToken);
    const contentById = new Map(contents.map(att => [att.id, att]));

    for (const { id, index } of indices) {
      const attachmentIds = attachmentsMap.get(id) || [];
      const subset = attachmentIds.map(attId => contentById.get(attId)).filter(Boolean) as AttachmentContentsResponse;
      if (!subset.length) continue;
      const { imageBlobs, imageUrls, allAttachments } = processAttachmentContents(subset);
      baseMessages[index].imageBlobs = imageBlobs;
      baseMessages[index].imageUrls = imageUrls;
      baseMessages[index].allAttachments = allAttachments;
      hydratedAttachmentMessageIdsRef.current.add(id);
    }
  };

  // Load messages and their attachments together (no delayed image hydration)
  const loadMessages = useCallback(async (chatId: number): Promise<void> => {
    // Helpers moved out to reduce cognitive complexity

    setIsLoading(true);
    setError(null);
    setCurrentChatId(chatId);
    setIsLoadingOlderAttachments(false);

    try {
      const accessToken = await getValidAccessToken();
      const apiMessages = await apiClient.getMessages(chatId, accessToken);

      // Prepare base messages (all), but only render last 5 initially
      const baseMessages = prepareBaseMessagesFromApi(apiMessages);
      baseMessagesRef.current = baseMessages;

      // Prepare attachment maps and order for lazy hydration
      const { order, attachmentsMap } = buildAttachmentIndex(apiMessages);
      messageOrderRef.current = order;
      attachmentsByMessageIdRef.current = attachmentsMap;
      hydratedAttachmentMessageIdsRef.current = new Set();

      // Initial hydration: last 5 messages with attachments (newest first)
      const lastFiveWithAttachments = getLastNWithAttachmentsIndices(apiMessages, 5);
      await hydrateByIndices(lastFiveWithAttachments, attachmentsMap, baseMessages, chatId, accessToken);

      // Single render: messages already hydrated with images (only last 5)
      if (currentChatIdRef.current !== chatId) return; // stale guard
      const initialSubset = computeInitialSubsetFromBase(baseMessages, 5);
      renderSubset(initialSubset);
    } catch (err) {
      await handleError(err, {
        setError,
        context: `Loading messages for chat ${chatId}`
      });
    } finally {
      setIsLoading(false);
    }
  }, [getValidAccessToken]);

  // Helpers to reduce cognitive complexity in lazy hydration
  const collectIdsForMessages = useCallback((
    attachmentsMap: Map<string, string[]>,
    msgIds: Set<string>
  ): string[] => {
    const toFetchIds: string[] = [];
    for (const msgId of msgIds) {
      const ids = attachmentsMap.get(msgId);
      if (ids?.length) toFetchIds.push(...ids);
    }
    return toFetchIds;
  }, []);

  const hydrateMessagesByIds = useCallback(async (
    justAddedIds: Set<string>,
    attachmentsMap: Map<string, string[]>,
    chatId: number,
    accessToken?: string
  ): Promise<void> => {
    if (currentChatIdRef.current !== chatId) return; // stale guard
    const toFetchIds = collectIdsForMessages(attachmentsMap, justAddedIds);
    if (!toFetchIds.length) return;

    const contents = await attachmentApi.getAttachment(toFetchIds, accessToken);
    const contentById = new Map(contents.map(att => [att.id, att]));

    for (const msgId of justAddedIds) {
      const ids = attachmentsMap.get(msgId) || [];
      const subset = ids.map(id => contentById.get(id)).filter(Boolean) as AttachmentContentsResponse;
      if (subset.length === 0) continue;
      const { imageBlobs, imageUrls, allAttachments } = processAttachmentContents(subset);
      dispatch({
        type: 'UPDATE_MESSAGE_ATTACHMENTS',
        payload: { messageId: msgId, imageBlobs, imageUrls, allAttachments }
      });
      hydratedAttachmentMessageIdsRef.current.add(msgId);
    }
  }, [collectIdsForMessages]);

  // Public helper to lazily load older attachments in batches (default size 5)
  const loadOlderAttachments = useCallback(async (batchSize: number = 5): Promise<void> => {
    if (isLoadingOlderAttachments) return;
    setIsLoadingOlderAttachments(true);
    try {
      const chatId = currentChatIdRef.current;
      if (!chatId) return;

      const accessToken = await getValidAccessToken();
      const baseMessagesAll = baseMessagesRef.current;
      const currentStart = loadedFromIndexRef.current; // first rendered index
      if (currentStart <= 0) { return; }

      const nextStart = Math.max(0, currentStart - batchSize);
      const slice = baseMessagesAll.slice(nextStart, currentStart); // chronological order

      // Prepend these older messages to the UI in one batch
      dispatch({ type: 'PREPEND_HISTORICAL_MESSAGES', payload: slice });
      loadedFromIndexRef.current = nextStart;

      // Attachments for these messages
      const attachmentsMap = attachmentsByMessageIdRef.current;
      const justAddedIds = new Set(slice.map(m => m.id));
      await hydrateMessagesByIds(justAddedIds, attachmentsMap, chatId, accessToken);
    } catch (err) {
      if (err instanceof ApiStatusError && err.isServerError()) {
        setErrorWithDetails(err.getUserMessage(), {
          title: err.title,
          detail: err.detail,
          traceId: err.traceId,
          instance: err.instance
        });
      } else {
        await handleError(err, {
          setError,
          context: 'Loading older attachments'
        });
      }
    } finally {
      setIsLoadingOlderAttachments(false);
    }
  }, [getValidAccessToken, isLoadingOlderAttachments, setErrorWithDetails, hydrateMessagesByIds]);

  // ...existing code...

  // Helper: centralized send error handling to reduce complexity in sendMessage
  const handleSendError = useCallback(async (err: unknown) => {
    if (err instanceof ApiStatusError && err.isServerError()) {
      setErrorWithDetails(err.getUserMessage(), {
        title: err.title,
        detail: err.detail,
        traceId: err.traceId,
        instance: err.instance
      });
    } else {
      await handleError(err, {
        setError,
        context: 'Sending message'
      });
    }
    dispatch({ type: 'SET_TYPING', payload: false });
  }, [setErrorWithDetails]);

  // Send a new message
  const sendMessage = useCallback(async (content: string, files?: File[], isInpaintingMode: boolean = false): Promise<void> => {
    if (isSendBlocked(content, files, state.isTyping)) return;

    // Clear any previous errors
    setError(null);
    setErrorDetails(undefined);
    
    // Save message details for potential retry
    setLastFailedMessage({
      content,
      files,
      isInpaintingMode
    });

    try {
      // Get access token
      const currentToken = await getValidAccessToken();
      
      // Create user message with attachments
      const { userMessage, attachmentsForApi } = await createUserMessageWithAttachments(content, files, isInpaintingMode);
      
      // Add user message immediately
      dispatch({ type: 'ADD_USER_MESSAGE', payload: userMessage });
      setError(null);

      // Send message to API
      // Determine request type based on inpainting mode and file presence
      const requestType = computeRequestType(files, isInpaintingMode);
      // Future support: "image_to_text" can be added here when needed

      // Use hardcoded portrait aspect ratio (3:4) as default
      const aspectRatio = '3:4';

      const messages = await chatApi.sendMessage(
        content.trim(),
        currentChatIdRef.current,
        userMessage.id,
        currentToken,
        attachmentsForApi,
        requestType,
        aspectRatio
      );

      // Handle API response
      await handleMessagesFromApi(messages, currentToken);

      // Clear failed message on successful send
      setLastFailedMessage(null);

      // Reload sidebar
      sideBarDispatcher({ type: 'RELOAD_SIDEBAR', payload: !sideBarState.reloadSideBar });

    } catch (err) {
      await handleSendError(err);
    }
  }, [state.isTyping, createUserMessageWithAttachments, processApiResponseMessages, getValidAccessToken, sideBarState.reloadSideBar, handleSendError]);

  // Clear messages (for new chat)
  const clearMessages = useCallback(() => {

    dispatch({ type: 'CLEAR_MESSAGES' });
    setCurrentChatId(null);
    setError(null);
  }, []); // Empty dependency array

  // Now the useEffect has stable dependencies
  useEffect(() => {
    if (initialChatId !== null && loadedInitialChatIdRef.current !== initialChatId) {
      loadedInitialChatIdRef.current = initialChatId;
      loadMessages(initialChatId);
    } else if (initialChatId === null && currentChatId !== null) {
      loadedInitialChatIdRef.current = null;
      clearMessages();
    } else if (initialChatId === null && currentChatId === null) {
      loadedInitialChatIdRef.current = null;
      setCurrentChatId(null);
    }
  }, [initialChatId, currentChatId, loadMessages, clearMessages]);

  // (setErrorWithDetails moved above to avoid TDZ and allow reuse)

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
    setErrorDetails(undefined);
  }, []);

  // Retry the last failed message
  const retryLastMessage = useCallback(async () => {
    if (!lastFailedMessage) {
      console.warn('No failed message to retry');
      return;
    }

    setIsRetrying(true);
    try {
      await sendMessage(
        lastFailedMessage.content, 
        lastFailedMessage.files, 
        lastFailedMessage.isInpaintingMode
      );
      // Clear the failed message and error on successful retry
      setLastFailedMessage(null);
      clearError();
    } catch (err) {
      console.error('Retry failed:', err);
      // Error will be handled by sendMessage's catch block
    } finally {
      setIsRetrying(false);
    }
  }, [lastFailedMessage, sendMessage, clearError]);

  return {
    messages: state.messages,
    isTyping: state.isTyping,
    isLoading,
    error,
    errorDetails,
    currentChatId,
    sideBarReload: sideBarState.reloadSideBar,
    isRetrying,
    loadMessages,
    sendMessage,
    clearMessages,
    retryLastMessage,
    clearError,
    loadOlderAttachments,
    isLoadingOlderAttachments,
  };
}
